import * as _ from 'lodash';
import { gql } from '@apollo/client';
import { useParams } from 'react-router';
import { getLaborMetrics } from '../constants/laborMetrics';
import { avg } from '../utils/aggregators';
import { asQuantityWithUnit, asYearString } from '../utils/formatters';
import { colorForCrop } from '../utils/colors';
import { CropDisplayFragment } from '../queries';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import {
  useDataTransforms,
  useFarmName,
  useFilterYears,
  useUserCanEditFarm,
  useYearsArray,
} from '../utils/hooks';
import {
  getLaborItems,
  getOperationsSummary,
  toOperationsTableData,
} from '../utils/operations';
import { useFilterContext } from '../contexts/FilterContext';
import { N_YEARS } from '../constants/years';
import useOpCategoryTabs from '../hooks/useOpCategoryTabs';
import MetricsHeader from '../components/layout/MetricsHeader';
import Content from '../components/layout/Content';
import SummarySection from '../components/layout/SummarySection';
import MetricsSummary from '../components/economics/MetricsSummary';
import DetailsSection from '../components/layout/DetailsSection';
import ItemsTable from '../components/tables/ItemsTable';

const ALL_CROPS = 'Total Hours';
const QUERY_NAME = 'LaborEconQuery';
const FRAGMENT_NAME = 'LaborEconFragment';

const LaborFragment = gql`
  ${CropDisplayFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    cropAggregates {
      crop {
        ...CropDisplayFields
      }
      operationAggregates {
        operation {
          id
          name
          category
        }
        hoursOfLaborByYear
      }
    }
  }
`;

const getSummaryMetrics = ({ cropAggregates }) =>
  [{ crop: { name: ALL_CROPS, isAggregate: true } }, ...cropAggregates].map(
    ({ crop }) => ({
      displayName: crop.name,
      accessor: crop.name,
      isAggregate: crop.isAggregate,
      aggregator: avg,
      color: colorForCrop(crop),
      defaultShown: true,
      format: asQuantityWithUnit('hrs'),
      info: '(Avg)',
      yAxisId: 'hrs',
    }),
  );

const toSummaryData = getOperationsSummary('hoursOfLaborByYear', ALL_CROPS);

const filterForYear = (item, year) => item.hoursOfLaborByYear[year] > 0;

const LaborView = () => {
  const { farmId } = useParams();
  const showLink = useUserCanEditFarm(farmId);
  const { msg, data } = useFarmOrSectionGroupData(
    LaborFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
  );
  const metricsForCategory = getLaborMetrics(showLink);

  const filterYears = useFilterYears();
  const {
    state: { year: filterYear, years },
  } = useFilterContext();
  const farmName = useFarmName();

  const allYears = useYearsArray(N_YEARS);

  const { activeCategory, opCategoryTabs } = useOpCategoryTabs();

  const [
    summaryData = {},
    summaryMetrics = [],
    tableData = {},
  ] = useDataTransforms(data, [
    toSummaryData,
    getSummaryMetrics,
    toOperationsTableData,
  ]);

  return (
    <>
      <MetricsHeader showCumulativeYearSelect showAnnualSingleYearSelect>
        Labor
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={summaryMetrics}
            data={summaryData}
            arrayTransform={filterYears}
            msg={msg}
          />
        </SummarySection>
        <DetailsSection>
          <ItemsTable
            data={tableData}
            cols={metricsForCategory(activeCategory, filterYear)}
            msg={msg}
            getItems={getLaborItems(
              activeCategory,
              filterYear,
              years,
              filterForYear,
            )}
            byCrop
            csvFileName={[
              'Labor',
              _.capitalize(activeCategory ?? 'All'),
              ...([asYearString(filterYear, years, allYears)] ?? []),
              farmName,
            ].join(' - ')}
            tableHeader={opCategoryTabs}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default LaborView;
